import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantLogo, useTenantColors } from '../../contexts/TenantBrandingContext';
import { useSmsFeature, useFreeTrialFeature } from '../../contexts/FeatureFlagContext';
import { api } from '../../lib/api';
import apiClient from '../../lib/api';

interface RegisterFormData {
  email: string;
  first_name: string;
  last_name: string;
  password: string;
  confirmPassword: string;
  company_name: string;
  vertical_pack: string;
  agreeToTerms: boolean;
  phone_number?: string;
  sms_consent?: boolean;
}

interface RegisterFormErrors {
  email?: string;
  first_name?: string;
  last_name?: string;
  password?: string;
  confirmPassword?: string;
  company_name?: string;
  vertical_pack?: string;
  agreeToTerms?: string;
  phone_number?: string;
}

export const RegisterPage: React.FC = () => {
  const { logoUrl, logoAlt } = useTenantLogo();
  const { primary } = useTenantColors();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSmsEnabled = useSmsFeature();
  const isFreeTrialEnabled = useFreeTrialFeature();
  
  // Add state to check if card is required for trial
  const [requireCardForTrial, setRequireCardForTrial] = useState(false);
  
  // Fetch feature flags on mount
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const response = await apiClient.get('/api/v1/system/features');
        setRequireCardForTrial(response.data.require_card_for_trial || false);
      } catch (error) {
        console.error('Failed to load features:', error);
      }
    };
    fetchFeatures();
  }, []);
  
  // Valid plan options
  const validPlans = ['starter', 'growth', 'agency'];
  
  // Get plan from URL params, normalize to lowercase, default to 'starter' if not specified or invalid
  const planFromUrl = (() => {
    const planParam = searchParams.get('plan');
    if (!planParam) return 'starter';
    const normalized = planParam.toLowerCase();
    return validPlans.includes(normalized) ? normalized : 'starter';
  })();
  
  const [formData, setFormData] = useState<RegisterFormData>({
    email: searchParams.get('email') || '',
    first_name: '',
    last_name: '',
    password: '',
    confirmPassword: '',
    company_name: searchParams.get('company_name') || '',
    vertical_pack: 'general',
    agreeToTerms: false,
    phone_number: '',
    sms_consent: false,
  });
  
  // Store plan separately since it's not part of formData
  const [selectedPlan, setSelectedPlan] = useState<string>(planFromUrl);
  const [errors, setErrors] = useState<RegisterFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  const verticalPacks = [
    { value: 'general', label: 'General Business' },
    { value: 'dental', label: 'Dental Practice' },
    { value: 'salon', label: 'Salon & Spa' },
    { value: 'medical', label: 'Medical & Healthcare' },
    { value: 'automotive', label: 'Automotive Service' },
    { value: 'veterinary', label: 'Veterinary Clinic' },
    { value: 'home_services', label: 'Home Services (HVAC/Plumbing/Electrical)' },
    { value: 'fitness', label: 'Fitness & Gym' },
  ];

  const validateForm = (): boolean => {
    const newErrors: RegisterFormErrors = {};
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.first_name) {
      newErrors.first_name = 'First name is required';
    }
    
    if (!formData.last_name) {
      newErrors.last_name = 'Last name is required';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.phone_number || formData.phone_number.trim() === '') {
      newErrors.phone_number = 'Phone number is required';
    } else {
      // Basic phone validation - remove non-digits and check length
      const phoneDigits = formData.phone_number.replace(/\D/g, '');
      if (phoneDigits.length < 10) {
        newErrors.phone_number = 'Phone number must contain at least 10 digits';
      }
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    // Validate plan - normalize and ensure it's valid
    const normalizedPlan = selectedPlan.toLowerCase();
    if (!validPlans.includes(normalizedPlan)) {
      // Invalid plan, default to starter
      setSelectedPlan('starter');
      console.warn(`Invalid plan '${selectedPlan}' detected, defaulting to 'starter'`);
    } else if (normalizedPlan !== selectedPlan) {
      // Normalize the plan if it's valid but not lowercase
      setSelectedPlan(normalizedPlan);
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    // Final plan validation and normalization before submission
    const normalizedPlan = selectedPlan.toLowerCase();
    if (!validPlans.includes(normalizedPlan)) {
      setErrors({ email: 'Invalid plan selected. Please refresh and try again.' });
      setSelectedPlan('starter'); // Reset to default
      return;
    }
    
    // Ensure plan is normalized
    const planToSubmit = normalizedPlan;
    
    setIsSubmitting(true);
    
    try {
      const response = await api.auth.register({
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        password: formData.password,
        company_name: formData.company_name || undefined,
        vertical_pack: formData.vertical_pack,
        phone_number: formData.phone_number || '',
        sms_consent: formData.sms_consent || false,
        plan: planToSubmit, // Include the normalized selected plan
      });
      
      // If payment is required (based on backend response or feature flag), 
      // store registration info temporarily for checkout after verification
      const paymentRequired = response.data.payment_required || (requireCardForTrial && isFreeTrialEnabled);
      if (paymentRequired) {
        localStorage.setItem('pending_registration', JSON.stringify({
          email: formData.email,
          requires_payment: true,
          billing_status: response.data.billing_status,
          plan: selectedPlan // Store the selected plan
        }));
      }
      
      setRegistrationSuccess(true);
    } catch (error: any) {
      console.error('Registration error:', error);
      
      // Handle different error types
      if (error.response?.status === 400) {
        const backendErrors = error.response.data?.detail || {};
        if (typeof backendErrors === 'string') {
          setErrors({ email: backendErrors });
        } else {
          setErrors(backendErrors);
        }
      } else {
        setErrors({ email: 'An error occurred. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof RegisterFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            {logoUrl && (
              <img
                className="mx-auto h-12 w-auto"
                src={logoUrl}
                alt={logoAlt}
              />
            )}
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a verification link to <strong>{formData.email}</strong>
            </p>
            {selectedPlan && (
              <p className="mt-3 text-sm text-blue-600 font-medium">
                You're signing up for the <span className="capitalize font-semibold">{selectedPlan}</span> plan trial
              </p>
            )}
            <p className="mt-4 text-sm text-gray-600">
              Please check your email and click the verification link to activate your account.
            </p>
            <p className="mt-4 text-sm text-gray-600">
              Didn't receive the email? You can also{' '}
              <Link
                to={`/verify-email?email=${encodeURIComponent(formData.email)}`}
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                verify manually here
              </Link>
              .
            </p>
            <div className="mt-6">
              <Link
                to="/login"
                className="font-medium text-indigo-600 hover:text-indigo-500"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          {logoUrl && (
            <img
              className="mx-auto h-12 w-auto"
              src={logoUrl}
              alt={logoAlt}
            />
          )}
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link
              to="/login"
              className="font-medium text-indigo-600 hover:text-indigo-500"
            >
              sign in to your existing account
            </Link>
          </p>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Name Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  First Name
                </label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  value={formData.first_name}
                  onChange={handleInputChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    errors.first_name ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="First name"
                />
                {errors.first_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.first_name}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Last Name
                </label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  value={formData.last_name}
                  onChange={handleInputChange}
                  className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                    errors.last_name ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="Last name"
                />
                {errors.last_name && (
                  <p className="mt-1 text-sm text-red-600">{errors.last_name}</p>
                )}
              </div>
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={handleInputChange}
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                placeholder="Email address"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Use an email with your business domain (e.g., name@yourbusiness.com) for faster Google Business verification
              </p>
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">
                Company Name (Optional)
              </label>
              <input
                id="company_name"
                name="company_name"
                type="text"
                value={formData.company_name}
                onChange={handleInputChange}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Your company name"
              />
            </div>

            {/* Vertical Pack */}
            <div>
              <label htmlFor="vertical_pack" className="block text-sm font-medium text-gray-700">
                Business Type
              </label>
              <select
                id="vertical_pack"
                name="vertical_pack"
                value={formData.vertical_pack}
                onChange={handleInputChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              >
                {verticalPacks.map(pack => (
                  <option key={pack.value} value={pack.value}>
                    {pack.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Phone Number - Required for business verification */}
            <div>
              <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <input
                id="phone_number"
                name="phone_number"
                type="tel"
                required
                value={formData.phone_number || ''}
                onChange={handleInputChange}
                className={`mt-1 appearance-none relative block w-full px-3 py-2 border ${
                  errors.phone_number ? 'border-red-300' : 'border-gray-300'
                } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                placeholder="+1 (555) 123-4567"
              />
              {errors.phone_number && (
                <p className="mt-1 text-sm text-red-600">{errors.phone_number}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Required for Google Business Profile verification
              </p>
            </div>

            {/* Optional SMS Consent - Only show if SMS is enabled */}
            {isSmsEnabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-start">
                  <input
                    id="sms_consent"
                    name="sms_consent"
                    type="checkbox"
                    checked={formData.sms_consent || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded mt-1"
                  />
                  <label htmlFor="sms_consent" className="ml-3 block text-sm text-gray-700">
                    <span className="font-medium">Optional:</span> I would like to receive SMS notifications and updates 
                    (not required to create account). Message & data rates may apply. Reply STOP to opt out anytime.
                  </label>
                </div>
                <p className="mt-2 text-xs text-gray-500 ml-7">
                  You can create your account and use all features without enabling SMS notifications.
                </p>
              </div>
            )}

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                    errors.password ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`appearance-none relative block w-full px-3 py-2 pr-10 border ${
                    errors.confirmPassword ? 'border-red-300' : 'border-gray-300'
                  } placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm`}
                  placeholder="Confirm password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
              )}
            </div>

            {/* Business Verification Notice */}
            <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-amber-800">Google Business Verification</h3>
                  <div className="mt-2 text-sm text-amber-700">
                    <p>To verify your Google Business Profile, use either:</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>A phone number that matches your business phone number on Google</li>
                      <li>An email address with your business domain (e.g., name@yourbusiness.com)</li>
                    </ul>
                    <p className="mt-2 font-medium">You can still use the app while verification is pending.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Payment Requirement Notice */}
            {!isFreeTrialEnabled && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">Payment Required</h3>
                    <div className="mt-2 text-sm text-blue-700">
                      <p>Payment information will be required after registration to access all features.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Terms Agreement */}
            <div className="flex items-center">
              <input
                id="agreeToTerms"
                name="agreeToTerms"
                type="checkbox"
                checked={formData.agreeToTerms}
                onChange={handleInputChange}
                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-900">
                I agree to the{' '}
                <a href="#" className="text-indigo-600 hover:text-indigo-500">
                  Terms and Conditions
                </a>{' '}
                and{' '}
                <a href="#" className="text-indigo-600 hover:text-indigo-500">
                  Privacy Policy
                </a>
              </label>
            </div>
            {errors.agreeToTerms && (
              <p className="text-sm text-red-600">{errors.agreeToTerms}</p>
            )}

            {/* Plan Confirmation Display */}
            {selectedPlan && selectedPlan !== 'starter' && (
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-blue-800">
                      Selected Plan: <span className="capitalize font-semibold">{selectedPlan}</span>
                    </p>
                    <p className="mt-1 text-xs text-blue-700">
                      You'll start your free trial on the {selectedPlan} plan
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Account...
                </div>
              ) : (
                isFreeTrialEnabled ? 'Start Free Trial' : 'Create Account'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
